import React, { useImperativeHandle, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { replayClass } from "./scrollToAnchor";

type Speed = "fast" | "base" | "slow";

type Props = React.HTMLAttributes<HTMLElement> & {
  as?: "div" | "span" | "p" | "main" | "tbody" | "li" | "ul";
  speed?: Speed;
  /** Adds a 4px rise to the opacity fade. */
  rise?: boolean;
  /**
   * Set: the node stays mounted and the fade replays when this value changes,
   * never on first mount. Unset: fades on mount, so pair it with `key`.
   */
  replayOn?: unknown;
  /** Suppresses the replay for this change, e.g. Back and Forward. */
  skip?: boolean;
};

const FADE = "motion-fade-in";

const FadeIn = React.forwardRef<HTMLElement, Props>(
  (
    {
      as: Tag = "div",
      speed = "base",
      rise = false,
      replayOn,
      skip = false,
      className,
      style,
      children,
      ...rest
    },
    forwarded,
  ) => {
    const ref = useRef<HTMLElement>(null);
    useImperativeHandle(forwarded, () => ref.current as HTMLElement);
    const replays = replayOn !== undefined;
    const skipRef = useRef(skip);
    skipRef.current = skip;
    const last = useRef(replayOn);

    useLayoutEffect(() => {
      if (Object.is(last.current, replayOn)) return;
      last.current = replayOn;
      const el = ref.current;
      if (!replays || !el || skipRef.current) return;
      replayClass(el, FADE);
    }, [replayOn, replays]);

    const vars = {
      "--motion-duration": `var(--motion-${speed})`,
      ...(rise ? { "--motion-rise": "4px" } : null),
    } as React.CSSProperties;

    return React.createElement(
      Tag,
      {
        ...rest,
        ref,
        className: cn(!replays && FADE, className),
        style: { ...vars, ...style },
      },
      children,
    );
  },
);
FadeIn.displayName = "FadeIn";

export default FadeIn;
